import type { Spec } from "@json-render/core";

const LEGACY_TABS_TYPE = "Tabs";
const LEGACY_ACCORDION_TYPE = "Accordion";

const LEGACY_STACK_PROPS = {
  direction: "vertical" as const,
  gap: "md" as const,
};

const LEGACY_CARD_PROPS_BASE = {
  description: null,
  maxWidth: null,
  centered: null,
  className: null,
};

interface TabFromLegacySpec {
  label?: string;
}

interface AccordionItemFromLegacySpec {
  title?: string;
  content?: string;
}

const isTab = (tab: unknown): tab is TabFromLegacySpec =>
  typeof tab === "object" && tab !== null;

const isAccordionItem = (item: unknown): item is AccordionItemFromLegacySpec =>
  typeof item === "object" && item !== null;

const legacyCardProps = (title: string | null) => ({
  ...LEGACY_CARD_PROPS_BASE,
  title,
});

/**
 * Rewrites retired Herald catalog types so stored specs still render after
 * components are removed from the active catalog. `Tabs` becomes a vertical
 * `Stack` of `Card` wrappers (titles from `props.tabs`) around each panel;
 * `Accordion` `props.items` become a `Stack` of `Card` nodes with a body
 * `Callout` per item. Does not mutate the input.
 *
 * @param {Spec | null} spec - json-render spec from a stored Herald message.
 * @returns Unchanged spec by reference when nothing was migrated; otherwise a shallow clone with updated elements.
 */
export const migrateLegacyHeraldSpec = (spec: Spec | null): Spec | null => {
  if (!spec?.elements || !spec.root) return spec;

  let elements = spec.elements;
  let changed = false;
  const addedElements: Spec["elements"] = {};

  for (const [key, el] of Object.entries(spec.elements)) {
    if (!el) continue;

    if (el.type === LEGACY_TABS_TYPE) {
      const rawTabs = (el.props as { tabs?: unknown } | undefined)?.tabs;
      const tabs = Array.isArray(rawTabs) ? rawTabs.filter(isTab) : [];
      const panelKeys = Array.isArray(el.children) ? el.children : [];
      const cardChildKeys: string[] = [];

      for (const [i, panelKey] of panelKeys.entries()) {
        const cardKey = `${key}__legacy-tab-card-${i}`;
        cardChildKeys.push(cardKey);
        const tabLabel = tabs[i]?.label;
        addedElements[cardKey] = {
          type: "Card",
          props: legacyCardProps(
            typeof tabLabel === "string" ? tabLabel : null,
          ),
          children: [panelKey],
        };
      }

      if (!changed) {
        elements = { ...spec.elements };
        changed = true;
      }
      elements[key] = {
        type: "Stack",
        props: LEGACY_STACK_PROPS,
        ...(cardChildKeys.length > 0 ? { children: cardChildKeys } : {}),
      };
      continue;
    }

    if (el.type !== LEGACY_ACCORDION_TYPE) continue;

    const rawItems = (el.props as { items?: unknown } | undefined)?.items;
    const items = Array.isArray(rawItems)
      ? rawItems.filter(isAccordionItem)
      : [];

    if (!changed) {
      elements = { ...spec.elements };
      changed = true;
    }

    const cardChildKeys: string[] = [];
    for (const [i, item] of items.entries()) {
      const cardKey = `${key}__legacy-accordion-card-${i}`;
      const calloutKey = `${cardKey}__body`;
      cardChildKeys.push(cardKey);
      addedElements[calloutKey] = {
        type: "Callout",
        props: {
          type: "info",
          title: null,
          content: typeof item.content === "string" ? item.content : "",
        },
      };
      addedElements[cardKey] = {
        type: "Card",
        props: legacyCardProps(
          typeof item.title === "string" ? item.title : null,
        ),
        children: [calloutKey],
      };
    }

    elements[key] = {
      type: "Stack",
      props: LEGACY_STACK_PROPS,
      ...(cardChildKeys.length > 0 ? { children: cardChildKeys } : {}),
    };
  }

  if (!changed) return spec;

  return {
    ...spec,
    elements: { ...elements, ...addedElements },
  };
};
